import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  feedbackEntryTypeLabel,
  feedbackEntryTypePluralLabel,
  intakeMyFeedbackState,
} from '@dha-nexus/shared';

import { recordChip } from '../feedbackMarkup/governedRecordChip.js';
import { listMyIntakes } from './supportTicketApi.js';
import './feedbackIntake.css';

// My Feedback typed-intake surface (typed help/intake framework, commits 2 and 4).
//
// A SEPARATE section rather than a new tab in the package lifecycle model. That is
// deliberate: My Feedback derives its Drafts/Submitted/Resolved tabs from screenshot
// PACKAGES and their linked governed records, and folding a second paginated source
// into those tabs would either mis-count them or require the unified read model that
// commit 6 owns (architecture review §8.16).
//
// Commit 4 widened it from support tickets to every typed intake the reporter owns.
// One server request, not one per vertical: the row already carries its type, and
// client-merging two paginated sources is the mistake §8.16 warns about.
//
// What this section must be is VISIBLE and TRUTHFUL. Delivery state comes from the
// aggregate intake response and is rendered from the server's own label — never
// a string this component composes — so it can never claim a ticket was sent.

const STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  collecting: 'In progress',
  ready_for_review: 'Ready to send',
  submitted: 'Sent to the product team',
  // Commit 5: `deflected` means PRIME answered the question itself and it was NOT
  // sent to anyone. "Answered" alone read as "someone answered you", so the label
  // says who — a user must be able to tell an AI answer from a human one at a
  // glance, and this row is the only place they see it after the fact.
  deflected: 'Answered by PRIME',
  cancelled: 'Cancelled',
});

function statusLabelFor(intake) {
  if (intake.intakeType === 'support_ticket' && intake.status === 'ready_for_review') {
    return intake.delivery?.mode === 'email' ? 'Ready to send' : 'Ready to record';
  }
  return STATUS_LABELS[intake.status] || intake.status;
}

// The submitted OUTCOME of a question, which the plain status cannot express: a
// submitted question is either one PRIME could not answer or one the reporter
// escalated after reading PRIME's answer.
const QUESTION_OUTCOME_STATUS = Object.freeze({
  handoff_submitted: 'Sent to the team — PRIME could not answer it',
  asked_team_anyway: 'Answered by PRIME, and sent to the team as well',
});

// The reviewer's queue and this section are two views over the same rows as of
// commit 6, so the type name comes from the ONE shared chokepoint
// (feedbackEntryTypeLabel) rather than a local map. A type PRIME does not
// recognize is labelled by its raw value and linked to My Feedback rather than
// guessed at — a wrong resume URL is worse than no link.
const TYPE_ROUTES = Object.freeze({
  support_ticket: '/feedback/support',
  feature_service_request: '/feedback/request',
});

function intakeHref(intake) {
  // A question lives on one route and carries its id as a query parameter, because
  // asking is not addressed per-question the way a form draft is.
  if (intake.intakeType === 'general_question') {
    return `/help/questions?intake=${encodeURIComponent(intake.id)}`;
  }
  const base = TYPE_ROUTES[intake.intakeType];
  return base ? `${base}/${intake.id}` : '/feedback';
}

// The honest one-line statement of where a typed intake IS. Only support tickets
// leave PRIME by email, so only they carry a delivery claim; a question additionally
// distinguishes "PRIME answered it" from "a person has it".
export function intakeStateLine(intake) {
  if (intake.intakeType === 'support_ticket') {
    return intake.status !== 'submitted'
      ? 'Not sent yet — this ticket is still a draft in PRIME'
      : intake.delivery?.statusLabel || 'Delivery state unavailable';
  }
  if (intake.intakeType === 'general_question') {
    if (intake.status === 'deflected') {
      return 'PRIME answered this itself — nobody was asked. You can still ask the team.';
    }
    if (intake.status === 'submitted') {
      return (
        QUESTION_OUTCOME_STATUS[intake.deflectionOutcome] || 'With the team in the feedback queue'
      );
    }
    return 'Not sent to anyone yet';
  }
  return intake.status === 'submitted'
    ? 'With the product team in the feedback queue'
    : 'Not sent yet — this request is still a draft in PRIME';
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    ...(sameYear ? {} : { year: 'numeric' }),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 25;

/**
 * The unified My Feedback rows for every typed intake the reporter owns.
 *
 * `typeFilter` is pushed to the SERVER so the page counts describe the filtered
 * set. `stateFilter` is applied here because the state is derived from the intake
 * status PLUS the linked governed record's status — there is no single column to
 * filter it on, and the shared classifier
 * (`intakeMyFeedbackState`) is the one place that derivation lives.
 */
export function MySupportTicketsSection({ typeFilter = '', stateFilter = '' } = {}) {
  const [state, setState] = useState({
    status: 'loading',
    tickets: [],
    error: null,
    hasMore: false,
    total: 0,
    // Which type filter PRODUCED these rows. The reset effect below is passive and
    // runs after the first render under a new filter — so for one frame the state
    // can still hold the previous filter's rows. Render compares this identity to
    // the live prop and suppresses mismatched rows, closing that frame (delta
    // re-audit of audit P1-1).
    forType: typeFilter,
    // OFFSET SEMANTICS: the next offset advances by the number of rows the SERVER
    // returned, before client de-duplication. Advancing by the deduped list length
    // instead made the window slip backwards whenever a row was inserted between
    // pages — the server would eventually report hasMore:false while rows the client
    // never received were still unshown ("Showing 50 of 51", no button). Stable
    // cursor pagination is the real answer and is out of scope here; this keeps the
    // window monotonic so the terminal state is reachable.
    nextOffset: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  // REQUEST EPOCH (audit P1-1). The type filter is a SERVER filter, so a change to
  // it starts a new request while the previous one may still be in flight — and
  // the rows already on screen were produced by the OLD filter. Every response
  // carries the epoch it was issued under and is dropped if that epoch is no
  // longer current, which closes both halves of the race: a first-page response
  // from an abandoned filter cannot replace the current rows, and a `Load more`
  // response from an abandoned filter cannot APPEND to them (the permanent half —
  // a transient wrong row disappears on the next response, merged rows do not).
  const requestEpochRef = useRef(0);

  // Paginated rather than a silent fixed cap (audit P2-3). The section used to ask for
  // 50 rows and render whatever came back, which quietly hid everything after the 50th
  // — materially reachable now that it lists every typed intake, not just support
  // tickets. The server's own `page.hasMore`/`page.total` drive the control, so the
  // count shown is the server's truth and not a guess from the array length.
  const loadPage = useCallback(
    async (offset) => {
      const response = await listMyIntakes({
        type: typeFilter || null,
        limit: PAGE_SIZE,
        offset,
      });
      const rows = response?.data || [];
      return {
        rows,
        hasMore: response?.page?.hasMore === true,
        total: Number(response?.page?.total ?? 0),
        // Pre-dedup count: what the server actually handed over from this offset.
        served: rows.length,
      };
    },
    [typeFilter],
  );

  useEffect(() => {
    const epoch = ++requestEpochRef.current;
    // Cleared SYNCHRONOUSLY, before the request is issued. Leaving the previous
    // filter's rows on screen "until the new ones arrive" is what let a reporter
    // read support tickets under the Questions heading.
    setState({
      status: 'loading',
      tickets: [],
      error: null,
      hasMore: false,
      total: 0,
      nextOffset: 0,
      forType: typeFilter,
    });
    setLoadingMore(false);
    (async () => {
      try {
        const page = await loadPage(0);
        if (epoch !== requestEpochRef.current) return;
        setState({
          status: 'ready',
          tickets: page.rows,
          error: null,
          hasMore: page.hasMore,
          total: page.total,
          nextOffset: page.served,
          forType: typeFilter,
        });
      } catch (error) {
        if (epoch !== requestEpochRef.current) return;
        setState({
          status: 'error',
          tickets: [],
          error,
          hasMore: false,
          total: 0,
          nextOffset: 0,
          forType: typeFilter,
        });
      }
    })();
    return () => {
      // Unmount or a filter change invalidates everything issued under this epoch.
      requestEpochRef.current += 1;
    };
  }, [loadPage, typeFilter]);

  async function loadMore() {
    const epoch = requestEpochRef.current;
    setLoadingMore(true);
    try {
      const page = await loadPage(state.nextOffset);
      // The filter moved while this page was in flight: these rows belong to a
      // set the reporter is no longer looking at.
      if (epoch !== requestEpochRef.current) return;
      setState((current) => ({
        ...current,
        // De-duplicated by id for DISPLAY: an insertion between pages makes the window
        // overlap, and the same row must not appear twice. The offset still advances by
        // what the server served, so the window never revisits ground it has covered.
        tickets: [
          ...current.tickets,
          ...page.rows.filter((row) => !current.tickets.some((existing) => existing.id === row.id)),
        ],
        hasMore: page.hasMore,
        total: page.total,
        nextOffset: current.nextOffset + page.served,
      }));
      // Keep the keyboard where the reporter was; the button stays put unless the list
      // is now complete.
      setTimeout(() => loadMoreRef.current?.focus(), 0);
    } catch (error) {
      if (epoch !== requestEpochRef.current) return;
      setState((current) => ({ ...current, error, hasMore: current.hasMore }));
    } finally {
      // A stale failure must not re-enable the control for the CURRENT filter,
      // whose own request may still be running.
      if (epoch === requestEpochRef.current) setLoadingMore(false);
    }
  }

  // The state filter is DERIVED, so it narrows what is rendered rather than what
  // is fetched. A cancelled intake belongs to no state and is therefore excluded
  // by every state view (never quietly filed under Drafts).
  // RENDER-TIME suppression of the one frame the passive reset effect cannot cover:
  // when the state was produced by a different type filter than the live prop, treat
  // the section as loading rather than paint the previous filter's rows.
  const stateIsCurrent = state.forType === typeFilter;
  const displayStatus = stateIsCurrent ? state.status : 'loading';
  const loadedTickets = stateIsCurrent ? state.tickets : [];

  const visibleTickets = stateFilter
    ? loadedTickets.filter((ticket) => intakeMyFeedbackState(ticket) === stateFilter)
    : loadedTickets;
  const hiddenByState = loadedTickets.length - visibleTickets.length;

  return (
    <section
      className="support-ticket-card"
      aria-labelledby="my-support-tickets-heading"
      data-testid="my-support-tickets"
    >
      <h2 id="my-support-tickets-heading">Tickets, requests, and questions</h2>
      <p className="support-ticket-card-hint">
        Problems you reported to the MHS Service Desk, features or services you asked the product
        team for, and questions you asked PRIME. Screenshot feedback stays in the tabs above.
      </p>

      {displayStatus === 'loading' ? (
        <p className="support-ticket-card-hint" role="status">
          Loading your tickets and requests…
        </p>
      ) : null}

      {displayStatus === 'error' ? (
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="my-support-tickets-error"
        >
          <h3>PRIME could not load your tickets and requests</h3>
          <p>{state.error?.message || 'The feedback service did not respond.'}</p>
        </div>
      ) : null}

      {/* An empty FILTERED result and an empty account are different facts. The
          global copy below is only true when nothing is narrowing the request:
          with a type filter active the reporter may well have tickets and
          requests, and "you have not reported anything" would be a lie
          (audit P1-1 AC-2). */}
      {displayStatus === 'ready' && loadedTickets.length === 0 && typeFilter ? (
        <p className="support-ticket-card-hint" data-testid="my-support-tickets-type-empty">
          No {feedbackEntryTypePluralLabel(typeFilter).toLowerCase()} match this filter. Clear the
          type filter to see everything you have reported or asked for.
        </p>
      ) : null}

      {displayStatus === 'ready' && loadedTickets.length === 0 && !typeFilter ? (
        <p className="support-ticket-card-hint" data-testid="my-support-tickets-empty">
          You have not reported a problem or asked for anything yet.{' '}
          <Link to="/feedback/support/new">Report a problem</Link>,{' '}
          <Link to="/feedback/request/new">request a feature or service</Link>, or{' '}
          <Link to="/help/questions">ask a question</Link>.
        </p>
      ) : null}

      {displayStatus === 'ready' && loadedTickets.length > 0 && visibleTickets.length === 0 ? (
        <p className="support-ticket-card-hint" data-testid="my-support-tickets-state-empty">
          None of your loaded tickets, requests, or questions are in this state.
          {state.hasMore ? ' There may be more to load.' : ''}
        </p>
      ) : null}

      {visibleTickets.length ? (
        <p className="support-ticket-card-hint" data-testid="my-support-tickets-count">
          {/* "Showing N" only. The server total can move between pages (a new intake),
              so pairing the two numbers as "N of TOTAL" is what produced the
              "Showing 50 of 51" dead end. The Load more control is driven by the
              server's own hasMore, which is the authoritative answer to
              "is there more". */}
          Showing {visibleTickets.length}
          {state.hasMore ? ' so far' : ''}
          {/* Said out loud, because the state filter runs over the LOADED window:
              a reporter must not read "Showing 2" as "you have 2". */}
          {hiddenByState > 0
            ? ` (${hiddenByState} loaded ${hiddenByState === 1 ? 'row is' : 'rows are'} in another state)`
            : ''}
          .
        </p>
      ) : null}

      {visibleTickets.length ? (
        <ul className="support-ticket-attachment-list" data-testid="my-support-tickets-list">
          {visibleTickets.map((ticket) => {
            const delivery = ticket.delivery;
            const href = intakeHref(ticket);
            const typeLabel = feedbackEntryTypeLabel(ticket.intakeType);
            // Only support tickets leave PRIME by email, so only they carry a
            // delivery claim. For every other type the honest statement is where the
            // request IS, not whether an email went out.
            const isSupportTicket = ticket.intakeType === 'support_ticket';
            const teamResponse = ticket.latestTeamResponse;
            const record = ticket.governedRecord;
            return (
              <li key={ticket.id} data-testid="my-support-ticket-row">
                <span>
                  <b>
                    <Link to={href}>{ticket.title || `Untitled ${typeLabel.toLowerCase()}`}</Link>
                  </b>
                  <br />
                  {/* Type, status, and both timestamps as TEXT — no status is
                      conveyed by color alone. */}
                  <span className="support-ticket-card-hint">
                    {typeLabel} · {statusLabelFor(ticket)} · created {formatWhen(ticket.createdAt)}{' '}
                    · updated {formatWhen(ticket.updatedAt)}
                  </span>
                  <br />
                  {/* Where the request WAS MADE FROM. A reviewer's queue row shows
                      the same route, so the two can be talked about together. */}
                  <span
                    className="support-ticket-card-hint"
                    data-testid={`my-support-ticket-area-${ticket.id}`}
                  >
                    {ticket.sourceRoute
                      ? `Reported from ${ticket.sourceRoute}`
                      : 'No application area recorded'}
                    {/* The governed record's own state, in the SAME words the
                        reviewer sees in the queue. */}
                    {record ? ` · Team status: ${recordChip(record).label}` : ''}
                    {record ? ` · ${record.id}` : ''}
                  </span>
                  <br />
                  <span
                    className="support-ticket-delivery-state"
                    data-glyph={
                      isSupportTicket
                        ? delivery?.deliveredExternally
                          ? '✓'
                          : 'i'
                        : ticket.status === 'submitted' || ticket.status === 'deflected'
                          ? '✓'
                          : 'i'
                    }
                    data-testid={`my-support-ticket-delivery-${ticket.id}`}
                  >
                    {intakeStateLine(ticket)}
                  </span>
                  {teamResponse ? (
                    <>
                      <br />
                      {/* The latest thing a PERSON said about this request. Server
                          projected, bounded, and marked when it was shortened —
                          never assembled from a status. */}
                      <span
                        className="support-ticket-card-hint"
                        data-testid={`my-support-ticket-response-${ticket.id}`}
                      >
                        <b>
                          PRIME product team
                          {teamResponse.createdAt ? ` · ${formatWhen(teamResponse.createdAt)}` : ''}
                          :
                        </b>{' '}
                        {teamResponse.body}
                        {teamResponse.truncated ? ' … (shortened)' : ''}
                      </span>
                    </>
                  ) : null}
                </span>
                <Link className="btn ghost sm" to={href}>
                  {ticket.status === 'submitted' ? 'View' : 'Continue'}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}

      {state.hasMore ? (
        <button
          type="button"
          className="btn ghost sm"
          data-testid="my-support-tickets-load-more"
          ref={loadMoreRef}
          disabled={loadingMore}
          onClick={() => void loadMore()}
        >
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </section>
  );
}

export default MySupportTicketsSection;
